// Day- and week-level readings of a yes/no (`bool`) feature for its charts.
//
// Pure functions over `/api/features/values/{id}` rows. The per-day rule
// mirrors `aggregations.bool_day_values` / `bool_feature.day_value` on the
// backend (see `docs/bool_features.md`):
//   * a DELETE tombstone is never an answer (the endpoint already drops them;
//     the filter here keeps the rule true for any caller);
//   * a day with no live row is unknown — absent from the map, never No;
//   * a day with live rows is Yes under `any` when one of them is 1, under
//     `all` only when every one is 1; otherwise No. The feature-page charts
//     always pass `any`; `all` is a per-summary setting (`params.day_rule`).

import type { BoolDayRule } from '../../utils/boolFeature';
import type { DateWindow } from '../windowRange';

export interface BoolValueRow {
  date: string;                 // ISO date or datetime; the leading yyyy-mm-dd is the day
  decimal_value: number | null;
  manual_override?: string | null;
}

/** Each recorded day's answer, keyed by 'YYYY-MM-DD'. Unknown days are absent. */
export function boolDayAnswers(rows: BoolValueRow[], dayRule: BoolDayRule): Map<string, boolean> {
  const byDay = new Map<string, number[]>();
  for (const r of rows) {
    if (r.manual_override === 'delete') continue;
    if (r.decimal_value == null) continue;
    const day = r.date.slice(0, 10);
    const list = byDay.get(day);
    if (list) list.push(Number(r.decimal_value));
    else byDay.set(day, [Number(r.decimal_value)]);
  }
  const out = new Map<string, boolean>();
  for (const [day, values] of byDay) {
    out.set(day, dayRule === 'all' ? values.every((v) => v === 1) : values.some((v) => v === 1));
  }
  return out;
}

const pad = (n: number) => String(n).padStart(2, '0');

export function isoDay(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function parseDay(iso: string): Date {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, d);
}

function addDays(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
}

export interface BoolDayCounts {
  yes: number;
  no: number;
  recorded: number;     // yes + no
  notRecorded: number;  // days in range with no answer — kept apart from No
}

/** Counts over the inclusive day range [from, to] ('YYYY-MM-DD'). */
export function countBoolDays(answers: Map<string, boolean>, from: string, to: string): BoolDayCounts {
  let yes = 0, no = 0, days = 0;
  if (from <= to) {
    for (let d = parseDay(from); isoDay(d) <= to; d = addDays(d, 1)) {
      days++;
      const a = answers.get(isoDay(d));
      if (a === true) yes++;
      else if (a === false) no++;
    }
  }
  return { yes, no, recorded: yes + no, notRecorded: days - yes - no };
}

/** Monday of the week containing `d` — the same week `windowRange` pages by. */
function mondayOf(d: Date): Date {
  const dow = d.getDay() === 0 ? 7 : d.getDay();
  return addDays(d, -(dow - 1));
}

export interface BoolWeek {
  weekStart: string; // Monday, 'YYYY-MM-DD'
  yes: number;       // Yes days that week
  no: number;        // No days that week
}

/**
 * Per-week Yes/No day counts for every Monday-anchored week that overlaps
 * `win`, whole weeks only (a week straddling a month boundary counts all
 * seven of its days). Weeks with no answers are included as zeros so gaps
 * show. An unbounded window spans the first to the last recorded week.
 * Nothing past the week containing `today` (or the last answer, if later).
 */
export function boolWeeks(
  answers: Map<string, boolean>,
  win: DateWindow,
  today: Date = new Date(),
): BoolWeek[] {
  if (answers.size === 0) return [];
  const days = [...answers.keys()].sort();
  const first = parseDay(days[0]);
  const last = parseDay(days[days.length - 1]);

  const start = Number.isFinite(win.start) ? new Date(win.start) : first;
  const lastDay = last > today ? last : today;
  const windowLast = Number.isFinite(win.end) ? addDays(new Date(win.end), -1) : last;
  const end = windowLast < lastDay ? windowLast : lastDay;

  const out: BoolWeek[] = [];
  for (let w = mondayOf(start); w <= end; w = addDays(w, 7)) {
    let yes = 0, no = 0;
    for (let i = 0; i < 7; i++) {
      const a = answers.get(isoDay(addDays(w, i)));
      if (a === true) yes++;
      else if (a === false) no++;
    }
    out.push({ weekStart: isoDay(w), yes, no });
  }
  return out;
}

/**
 * A yes/no feature's raw per-checkin series (`/api/features/timeseries`, each
 * point at local midnight of its day) collapsed to one point per recorded day
 * under `dayRule`: 1 for a Yes day, 0 for a No day, in day order. A summary's
 * Trend line plots this rather than every checkin, so a day with a "no" and a
 * "yes" reads as one answer, the same one the summary's numbers count.
 */
export function boolDaySeries(
  points: Array<{ date: number; value: number }>,
  dayRule: BoolDayRule,
): Array<{ date: number; value: number }> {
  const answers = boolDayAnswers(
    points.map((p) => ({ date: isoDay(new Date(p.date)), decimal_value: p.value })),
    dayRule,
  );
  return [...answers.entries()]
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    .map(([day, yes]) => ({ date: parseDay(day).getTime(), value: yes ? 1 : 0 }));
}
